import { type FormEvent, useEffect, useId, useRef, useState } from "react";

/**
 * Inviting somebody into the family (issue #303).
 *
 * ## The consequence lands before the address is collected
 *
 * This is the whole reason the form has copy of its own, and it is the one place on this
 * surface where panel-authored words are the right answer. `invite_member`'s propose message
 * says what an invitation costs — but a propose message arrives *after* somebody has typed an
 * address and pressed send, and `how_to_start` already settled that the disclosure belongs
 * **before** the email, not after (#227's review): an invitation hands over everything the
 * family has ever told Boone, retrospectively, and there is no taking that back.
 *
 * The confirmation that follows is still the server's own sentence, unedited — see
 * {@link Confirm}. This says what a person needs in order to decide whether to type an address
 * at all; that says what the server is about to do.
 *
 * ## One field, and no role picker
 *
 * `invite_member` takes a `role`, and this always sends `adult` (`api/roster.ts`). Offering
 * `owner` here would mean handing co-ownership to an address before anybody has confirmed it
 * belongs to the person meant — and the honest route to it already exists on the roster, as
 * *Make owner* on a member who has actually arrived.
 *
 * ## It validates almost nothing, deliberately
 *
 * An address is required and that is all this checks. `invite_member`'s own schema is the
 * bound (#119), and it is stricter than anything worth reimplementing in a browser — while a
 * client-side pattern that is stricter than the server's would refuse an address the product
 * would happily have sent to.
 */
export function InviteForm({
  pending,
  locked,
  error,
  onSubmit,
  onCancel,
}: {
  pending: boolean;
  /**
   * A proposal is open on the page behind this form.
   *
   * Separate from {@link pending} because the two mean different things to a person: `pending`
   * is *this* form working and shows it in the button, while `locked` is the confirmation
   * standing in front of everything. Without it the form stays live behind the scrim, and
   * Shift+Tab out of the dialog reaches it — the hazard `Confirm.tsx` records.
   */
  locked: boolean;
  /** The server's own refusal, when there is one — a full family, a spent allowance. */
  error: string | null;
  onSubmit: (email: string) => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState("");
  const emailId = useId();
  const fieldRef = useRef<HTMLInputElement>(null);

  // Opening the form moves focus into it: the trigger unmounts when this appears, so a
  // keyboard user was otherwise dumped to `<body>`. `FactForm` does the same.
  useEffect(() => {
    fieldRef.current?.focus();
  }, []);

  const trimmed = email.trim();
  const ready = trimmed.length > 0;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready || pending) return;
    onSubmit(trimmed);
  }

  return (
    <form className="inviteform" onSubmit={submit}>
      <h2 className="inviteform__title">Invite someone into the family</h2>

      <div className="field">
        <label className="field__label" htmlFor={emailId}>
          Their email address
        </label>
        <input
          className="field__input"
          id={emailId}
          ref={fieldRef}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          disabled={locked}
          placeholder="their@email.com"
          /* `INPUT_LIMITS.email`. Capping an <input> is safe here where capping a textarea is
             not: an address is short and typed, never pasted prose that would be silently
             clipped (#119's "refuse, never truncate" is about the family's own words). */
          maxLength={320}
          autoComplete="off"
        />
      </div>

      <div className="inviteform__cost">
        <p>
          Whoever opens that link will be able to read everything this family has ever told Boone,
          including things said long before they arrived. There is no way to take that back
          afterwards — only to remove their access later.
        </p>
      </div>

      {error === null ? null : (
        <p className="inviteform__error" role="alert">
          {error}
        </p>
      )}

      <div className="inviteform__actions">
        <button
          className="cta cta--quiet"
          type="button"
          onClick={onCancel}
          disabled={pending || locked}
        >
          Cancel
        </button>
        <button className="cta" type="submit" disabled={!ready || pending || locked}>
          {pending ? "Sending…" : "Send the invitation"}
        </button>
      </div>
    </form>
  );
}
